<template>
  <div :class="componentClassName">
    <div class="t-default-layout__nav">
      <o-global-sidebar
        :key="sidebarKey"
        :navigation-items="navigationItems"
        :current-user-info="currentUserInfo"
        :handle-online-status-change="handleOnlineStatusChange"
        :handle-logout="handleLogout"
        :username="username"
        :company-i-d="companyID"
      ></o-global-sidebar>
    </div>
    <div class="t-default-layout__main">
      <slot></slot>
    </div>
    <o-calling-modal
      v-if="currentCallInfo"
      :is-fullscreen="currentCallInfo.isFullscreen"
      :microphone-list-options="currentCallInfo.microphoneListOptions"
      :speaker-list-options="currentCallInfo.speakerListOptions"
      :microphone="currentCallInfo.microphone"
      :speaker="currentCallInfo.speaker"
      :handle-microphone-selection-change="currentCallInfo.handleMicrophoneSelectionChange"
      :handle-speaker-selection-change="currentCallInfo.handleSpeakerSelectionChange"
      :status="currentCallInfo.status"
      :calling-time="currentCallInfo.callingTime"
      :response-status-list="currentCallInfo.responseStatusList"
      :customer-info="currentCallInfo.customerInfo"
      :pending-active-id="currentCallInfo.pendingActiveId"
      :pending-available-list="currentCallInfo.pendingAvailableList"
      :memo="currentCallInfo.memo"
      :memo-updated="currentCallInfo.memoUpdated"
      :user-list="currentCallInfo.userList"
      :forwarding-user-list="currentCallInfo.forwardingUserList"
      :handle-call-history-button-click="currentCallInfo.handleCallHistoryButtonClick"
      :handle-contact-list-button-click="currentCallInfo.handleContactListButtonClick"
      :handle-hold-button-click="currentCallInfo.handleHoldButtonClick"
      :handle-transfer-call-submit="currentCallInfo.handleTransferCallSubmit"
      :handle-numpad-click="currentCallInfo.handleNumpadClick"
      :handle-end-call-button-click="currentCallInfo.handleEndCallButtonClick"
      :handle-end-form-submit="currentCallInfo.handleEndFormSubmit"
      :handle-onhold-activate-button-click="currentCallInfo.handleOnholdActivateButtonClick"
      :handle-onhold-deactivate-button-click="currentCallInfo.handleOnholdDeactivateButtonClick"
      :is-loading="currentCallInfo.isLoading"
      :is-transfer-count-exceeded="currentCallInfo.isTransferCountExceeded"
      :is-hold-count-exceeded="currentCallInfo.isHoldCountExceeded"
      :is-transfer-ok="currentCallInfo.isTransferOk"
      :current-user-info="currentUserInfo"
      :push-number-setting-name="currentCallInfo.pushNumberSettingName"
      :is-acw="currentCallInfo.isAcw"
      :handle-close-call="currentCallInfo.handleCloseCall"
      :created-case-id="currentCallInfo.createdCaseId"
      :sf-current-info="currentCallInfo.sfCurrentInfo"
      :tel-number-info="currentCallInfo.telNumberInfo"
      @update-client-list="updateClientList"
      @update-calling-modal-fullscreen="changeFullscreenState"
      @update-memo-empty-status="changeMemoEmptyStatus"
      @sync-memo="syncMemo"
    />
    <o-incoming-call-modal
      v-if="currentIncomingCallInfo"
      :customer-info="currentIncomingCallInfo.customerInfo"
      :response-status-list="currentIncomingCallInfo.responseStatusList"
      :has-new-call="currentIncomingCallInfo.hasNewCall"
      :has-new-on-hold="currentIncomingCallInfo.hasNewOnHold"
      :onhold-list="currentIncomingCallInfo.onholdList"
      :handle-incoming-call-tab-change="currentIncomingCallInfo.handleIncomingCallTabChange"
      :handle-reject-call-click="currentIncomingCallInfo.handleRejectCallClick"
      :handle-receive-call-click="currentIncomingCallInfo.handleReceiveCallClick"
      :handle-onhold-button-click="currentIncomingCallInfo.handleOnholdButtonClick"
      :sf-cases="currentIncomingCallInfo.sfCases"
      :sf-contact-info="currentIncomingCallInfo.sfContactInfo"
      :push-number-setting-name="currentIncomingCallInfo.pushNumberSettingName"
      :tel-number-info="currentIncomingCallInfo.telNumberInfo"
    />
    <o-incoming-call-modal v-if="!currentIncomingCallInfo && !currentCallInfo" />

    <o-notice
      v-if="showNotice"
      footer-variant="confirmation"
      width="540px"
      height="auto"
      :notice-list="noticeList"
      :footer-shadow="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    ></o-notice>

    <teleport to="body">
      <o-modal
        id="DefaultLayout_resultModal"
        v-model:visible="resultModalVisible"
        class="o-modal-remove-phone-number-confirmation"
        footer-variant="confirmation"
        width="442px"
        :footer-shadow="false"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
      >
        <template #title>{{ resultModalTitle }}</template>
        <a-text>
          <!-- eslint-disable -->
        <span v-html="resultModalContent" />
          <!-- eslint-enable -->
        </a-text>
        <template #footer>
          <a-button
            id="close-modal-button"
            variant="block"
            color="primary"
            label="OK"
            size="small"
            @click="handleCloseModal"
          />
        </template>
      </o-modal>
    </teleport>
  </div>
</template>

<script>
import { mapModifiers } from '@/libs/component';
import OGlobalSidebar from '@/components/organisms/global-sidebar';
import OCallingModal from '@/components/organisms/calling-modal';
import OIncomingCallModal from '@/components/organisms/incoming-call-modal';
import ONotice from '@/components/organisms/notice';
import { NAVIGATION_ITEMS } from '@/libs/constants';
import { POINT } from '@/libs/constants';
import { cloneDeep } from 'lodash';

import { utils } from '@/common/utils.js';
import { messageContent } from '@/common/messageContent.js';
import { customRequest } from '@/common/customRequest.js';

export default {
  name: 't-default-layout',
  components: {
    OGlobalSidebar,
    OCallingModal,
    OIncomingCallModal,
    ONotice,
  },
  mixins: [messageContent, customRequest],
  props: {
    class: {
      type: String,
      default: '',
    },
    activeNavId: {
      type: String,
      default: '',
    },
    currentCallInfo: {
      type: Object,
      default: null,
    },
    currentIncomingCallInfo: {
      type: Object,
      default: null,
    },
    handleOnlineStatusChange: {
      type: Function,
      default: () => {},
    },
    handleLogout: {
      type: Function,
      default: () => {},
    },
    username: {
      type: String,
      default: '',
    },
    companyID: {
      type: String,
      default: '',
    },
    userID: {
      type: String,
      default: '',
    },
    currentUserInfo: {
      type: Object,
      default: null,
    },
    noticeList: {
      type: Array,
      default: new Array(0),
    },
  },
  emits: ['update-client-list', 'update-calling-modal-fullscreen', 'update-memo-empty-status', 'sync-memo'],
  data() {
    return {
      showNotice: false,
      resultModalTitle: '',
      resultModalContent: '',
      resultModalVisible: false,
      voiceToTextOn: false,
      navigationItems: [],
      sidebarKey: 0,
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers('t-default-layout');
      return `${baseClassName} ${this.class}`.trim();
    },
    isComUser() {
      return this.currentUserInfo.company_id === POINT;
    },
    isGeneralUser() {
      return this.currentUserInfo.company_id !== POINT;
    },
    isManager() {
      return this.currentUserInfo.auth_info === 'manager';
    },
    isOperator() {
      return this.currentUserInfo.auth_info === 'operator';
    },
    isSoOperator() {
      return this.currentUserInfo.auth_info === 'soOperator';
    },
    isComOperator() {
      return this.isComUser && this.isOperator;
    },
    isComSoOperator() {
      return this.isComUser && this.isSoOperator;
    },
    isGeneralOperator() {
      return this.isGeneralUser && this.isOperator;
    },
  },
  watch: {
    voiceToTextOn(val, old) {
      this.navigationItems = this.getNavigationItems();
      // サイドバーを再描画
      this.sidebarKey++;
    },
    noticeList(val) {
      if (val && val.length > 0) {
        this.showNotice = true;
      }
    },
  },
  async created() {
    this.navigationItems = this.getNavigationItems();
    await this.handleFetchVoiceToTextOn();
    if (this.noticeList && this.noticeList.length > 0) this.showNotice = true;
  },
  methods: {
    getNavigationItems() {
      const navigationItems = cloneDeep(NAVIGATION_ITEMS);
      if (this.isGeneralUser) {
        const navigationItemsIndex = navigationItems.findIndex(({ nav_id }) => nav_id === 'settings');
        navigationItems[navigationItemsIndex].items = navigationItems[navigationItemsIndex].items.filter(
          item =>
            item.nav_id !== 'settings-company-list' &&
            item.nav_id !== 'settings-request' &&
            // item.nav_id !== 'price-history-list-mgmt' &&
            item.nav_id !== 'number-mgmt' &&
            item.nav_id !== 'number-mgmt-fdnd' // #3112
        );
      }

      // メニュー表示判別処理
      for (let i = 0; i < navigationItems.length; i++) {
        // 発着信履歴
        if (navigationItems[i].nav_id === 'call_history') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // 連絡先
        if (navigationItems[i].nav_id === 'contact') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // アウトバウンド
        if (navigationItems[i].nav_id === 'outbound-list') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // 設定
        if (navigationItems[i].nav_id === 'settings') {
          navigationItems[i] = this.processSettingNavItems(navigationItems[i]);
        }
      }

      return navigationItems;
    },
    processSettingNavItems(settings) {
      for (let j = 0; j < settings.items.length; j++) {
        // 料金状況
        if (settings.items[j].nav_id === 'price-history-list') {
          // 003300のオペレーター権限（SO）と003300以外のオペレーター権限は非表示
          if (this.isComSoOperator || this.isGeneralOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 料金状況管理
        if (settings.items[j].nav_id === 'price-history-list-mgmt') {
          // 003300のオペレーター権限と003300以外は非表示
          if (this.isComOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 営業時間設定
        if (settings.items[j].nav_id === 'settings-business-hours') {
          // 003300のオペレーター権限（SO）は非表示
          if (this.isComSoOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 着信フロー設定
        if (settings.items[j].nav_id === 'settings-incoming-flows') {
          // 003300のオペレーター権限（SO）は非表示
          if (this.isComSoOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 通録テキスト化設定(PoC対応)
        if (settings.items[j].nav_id === 'settings-voice-to-text') {
          // トライアルアカウントまたは通録テキスト化機能がOFFの場合は非表示
          if (utils.chkTrialCompanyID(this.companyID) || !this.voiceToTextOn) {
            settings.items.splice(j, 1);
          }
        }

        // 契約情報確認・変更
        if (settings.items[j].nav_id === 'settings-contract') {
          // 003300のオペレーター権限（SO）と003300以外のオペレーター権限は非表示
          if (this.isComSoOperator || this.isGeneralOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 企業情報管理
        if (settings.items[j].nav_id === 'settings-company-list') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 申し込み管理
        if (settings.items[j].nav_id === 'settings-request') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 電話番号管理
        if (settings.items[j].nav_id === 'number-mgmt') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // #3112
        // FD/ND番号管理
        if (settings.items[j].nav_id === 'number-mgmt-fdnd') {
          // 003300のオペレーター権限と003300以外は非表示
          if (this.isComOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 電話番号管理(一般ユーザー用)
        if (settings.items[j].nav_id === 'number-mgmt-general') {
          // オペレーター権限と003300は非表示
          // #3445 トライアルユーザも非表示
          if (this.isComUser || this.isOperator || utils.chkTrialCompanyID(this.currentUserInfo.company_id)) {
            settings.items.splice(j, 1);
          }
        }

        // #2945
        // トライアル管理
        if (settings.items[j].nav_id === 'settings-trial') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // メール配信管理
        if (settings.items[j].nav_id === 'settings-sendmail') {
          // 003300のSOオペレーター権限と003300以外は非表示
          if (this.isComSoOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }
      }
      return settings;
    },
    updateClientList() {
      this.$emit('update-client-list');
      //this.$emit('update-client-list');
    },
    changeFullscreenState(flag) {
      this.$emit('update-calling-modal-fullscreen', flag);
    },
    changeMemoEmptyStatus(flag) {
      this.$emit('update-memo-empty-status', flag);
    },
    syncMemo(syncData) {
      this.$emit('sync-memo', syncData);
    },
    showErrorModal(message) {
      this.resultModalVisible = true;
      this.resultModalTitle = messageContent.errorModalTitle;
      this.resultModalContent = message;
    },
    handleCloseModal() {
      this.resultModalVisible = false;
      this.resultModalTitle = '';
      this.resultModalContent = '';
    },
    handleFetchVoiceToTextOn: async function () {
      await this.postRequest('/voicetotext')
        .then(data => {
          if (data.status === 'success') {
            if (data.data) {
              this.voiceToTextOn = data.data.voiceToTextOn ? data.data.voiceToTextOn : false;
            } else {
              this.voiceToTextOn = false;
            }
          } else {
            this.showErrorModal(messageContent.requireTry('通話録音テキスト化設定の取得'));
          }
        })
        .catch(error => {
          this.showErrorModal(messageContent.requireTry('通話録音テキスト化設定の取得'));
        });
    },
  },
};
</script>
